import { FC, ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@contexts/AuthContext";
import { UserRole, Permission } from "../../types/user";
import {
  canAccessRoute,
  hasPermission,
  extractUserRole,
  getDefaultRouteForRole,
} from "../../utils/role-utils";
import ErrorBoundary from "../common/ErrorBoundary";
import LoadingFallback from "../common/LoadingFallback";

interface RoleProtectedRouteProps {
  children: ReactNode;
  requiredRole?: UserRole | UserRole[];
  requiredPermission?: Permission;
  fallbackRoute?: string;
  allowedRoles?: UserRole[];
}

const RoleProtectedRoute: FC<RoleProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredPermission,
  fallbackRoute,
  allowedRoles,
}) => {
  const { user, isAuthenticated, emailVerified, isLoading } = useAuth();
  const location = useLocation();

  // First check basic authentication
  if (isLoading) {
    return <LoadingFallback message="Checking authentication..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!emailVerified) {
    return <Navigate to="/verify-email" replace />;
  }

  // Extract user role
  const userRole = extractUserRole(user);

  if (!userRole) {
    console.warn("⚠️ No user role found, redirecting to login");
    return <Navigate to="/login" replace />;
  }

  // Check role-based access
  let hasAccess = true;

  // Check if user has required role
  if (requiredRole) {
    if (Array.isArray(requiredRole)) {
      hasAccess = requiredRole.includes(userRole);
    } else {
      hasAccess = userRole === requiredRole;
    }
  }

  // Check if user has required permission
  if (requiredPermission && hasAccess) {
    hasAccess = hasPermission(userRole, requiredPermission);
  }

  // Check if user role is in allowed roles
  if (allowedRoles && hasAccess) {
    hasAccess = allowedRoles.includes(userRole);
  }

  // Check if user can access current route
  if (hasAccess) {
    hasAccess = canAccessRoute(userRole, location.pathname);
  }

  // If no access, redirect to appropriate route
  if (!hasAccess) {
    const redirectTo = fallbackRoute || getDefaultRouteForRole(userRole);
    console.warn(
      `⚠️ User role ${userRole} does not have access to ${location.pathname}, redirecting to ${redirectTo}`,
    );
    return <Navigate to={redirectTo} replace />;
  }

  return <ErrorBoundary>{children}</ErrorBoundary>;
};

export default RoleProtectedRoute;
